/* global conversionTarget, grecaptcha, gtag, pricingRequestSuccessMsg, isGoogleAnalyticsEnabled, isTns, advertiseBrandName, t5RecaptchaScriptUrl -- provided by the page (a Blade-injected value or a third-party script tag), not declared in this file */
import axios from 'axios';
import { backendErrorHandler, errorHandler } from '@common/errors';
import { failure } from '@common/alert';
import { loadScript } from '@common/lazy-assets';
import TagManager from '@common/tagmanager';

$(() => {
    /*
     * reCAPTCHA is 333KB and only matters once someone submits the form, so it
     * is fetched on first interaction rather than at page load.
     *
     * `api.js` auto-renders every `.g-recaptcha` element when it executes, and
     * this page's widget is invisible (`data-size="invisible"`), so the render
     * must have happened before `grecaptcha.execute()` is called. `grecaptcha.ready()`
     * is the documented signal for that.
     */
    let recaptchaReady = null;

    const loadRecaptcha = () => {
        if (recaptchaReady !== null) {
            return recaptchaReady;
        }

        /*
         * `loadScript` drops its own cache when a fetch fails, but this wrapper
         * would keep handing out the rejected promise forever -- one blip and
         * the form could never be submitted again. Clearing the cache lets the
         * next attempt retry; the identity check avoids clobbering a retry that
         * has already started.
         */
        const pending = loadScript(t5RecaptchaScriptUrl)
            .then(() => new Promise((resolve) => {
                if (typeof grecaptcha === 'undefined' || typeof grecaptcha.ready !== 'function') {
                    resolve();
                    return;
                }

                grecaptcha.ready(resolve);
            }))
            .catch((error) => {
                if (recaptchaReady === pending) {
                    recaptchaReady = null;
                }

                throw error;
            });

        recaptchaReady = pending;

        return pending;
    };

    /*
     * Warm the fetch as soon as there's a signal the form will be used, so the
     * network round trip overlaps with the user still typing.
     */
    $('#form-advertise').one('focusin', () => {
        loadRecaptcha().catch(() => {
            // Swallowed: the submit handler surfaces the failure to the user.
        });
    });

    const getAdvertiseTestData = (url) => {
        return axios.get(url).then(response => response.data);
    };

    const initQuickConnectCarousel = () => {
        const $carousel = $('[data-quickconnect-carousel]');

        if (!$carousel.length) {
            return;
        }

        const $cards = $carousel.find('[data-quickconnect-card]');

        if ($cards.length < 2) {
            $carousel.find('[data-quickconnect-prev], [data-quickconnect-next]').hide();
            return;
        }

        let activeIndex = Math.floor($cards.length / 2);
        let activePointerId = null;
        let pointerStartX = 0;
        let pointerStartY = 0;
        const SWIPE_THRESHOLD = 40;

        const updateCards = () => {
            $cards.each((cardIndex, card) => {
                const $card = $(card);
                const relativeIndex = (cardIndex - activeIndex + $cards.length) % $cards.length;

                $card
                    .removeClass('is-before is-active is-after is-hidden')
                    .attr('aria-hidden', false);

                if (relativeIndex === 0) {
                    $card.addClass('is-active');
                    return;
                }

                if (relativeIndex === 1) {
                    $card.addClass('is-after');
                    return;
                }

                if (relativeIndex === $cards.length - 1) {
                    $card.addClass('is-before');
                    return;
                }

                $card
                    .addClass('is-hidden')
                    .attr('aria-hidden', true);
            });
        };

        const showPreviousCard = () => {
            activeIndex = (activeIndex - 1 + $cards.length) % $cards.length;
            updateCards();
        };

        const showNextCard = () => {
            activeIndex = (activeIndex + 1) % $cards.length;
            updateCards();
        };

        $carousel.find('[data-quickconnect-prev]').on('click', showPreviousCard);
        $carousel.find('[data-quickconnect-next]').on('click', showNextCard);

        $carousel.on('pointerdown', (event) => {
            const pointerEvent = event.originalEvent;

            if (!pointerEvent.isPrimary || pointerEvent.button > 0) {
                return;
            }

            if ($(event.target).closest('button, a, input, select, textarea, [role="button"]').length) {
                return;
            }

            activePointerId = pointerEvent.pointerId;
            pointerStartX = pointerEvent.clientX;
            pointerStartY = pointerEvent.clientY;
            $carousel[0].setPointerCapture(pointerEvent.pointerId);
        });

        $carousel.on('pointerup', (event) => {
            const pointerEvent = event.originalEvent;

            if (pointerEvent.pointerId !== activePointerId) {
                return;
            }

            activePointerId = null;
            const distanceX = pointerEvent.clientX - pointerStartX;
            const distanceY = pointerEvent.clientY - pointerStartY;

            if (Math.abs(distanceX) < SWIPE_THRESHOLD || Math.abs(distanceX) <= Math.abs(distanceY)) {
                return;
            }

            if (distanceX > 0) {
                showPreviousCard();
                return;
            }

            showNextCard();
        });

        $carousel.on('pointercancel', () => {
            activePointerId = null;
        });

        updateCards();
    };

    initQuickConnectCarousel();

    $('#advertise-test-data').on('click', function() {
        const $btn = $(this);
        const $form = $('#form-advertise');

        getAdvertiseTestData($btn.data('source'))
            .then((testData) => {
                $form.find(':input[name=company_name]').val(testData.company_name);
                $form.find(':input[name=contact_name]').val(testData.contact_name);
                $form.find(':input[name=email]').val(testData.email);
                $form.find(':input[name=phone]').val(testData.phone);
                $form.find(':input[name=message]').val(testData.message);
            })
            .catch(errorHandler);
    });

    const trackAdvertiseSuccess = () => {
        if (isGoogleAnalyticsEnabled && !isTns) {
            gtag('event', 'click', {
                'event_category': 'Pricing Requests',
                'event_label': 'Request Pricing',
            });
        }

        if (conversionTarget && !isTns) {
            gtag('event', 'conversion', {
                send_to: conversionTarget,
            });
        }

        if (isTns) {
            if (document.referrer.match(/linkedin.com/) !== null) {
                TagManager.advertiseRequestSubmittedLinkedin();
            } else {
                TagManager.advertiseRequestSubmitted();
            }
        }
    };

    const $successModal = $('#advertise-success-modal');
    const $successHeadline = $successModal.find('.react-page-confirmation-modal__headline');
    const $successMessage = $successModal.find('.react-page-confirmation-modal__message');

    const openAdvertiseSuccessModal = ({
        headline = `Thanks for your interest in ${advertiseBrandName}!`,
        message = 'Our sales team will be in touch shortly to set-up a meeting.',
    } = {}) => {
        $successHeadline.text(headline);
        $successMessage.text(message);

        $successModal.modal('show');
    };

    if (pricingRequestSuccessMsg.length > 0) {
        trackAdvertiseSuccess();

        openAdvertiseSuccessModal();
    }

    $('[data-toggle="tooltip"]').tooltip({
        trigger: 'hover',
        placement: 'bottom',
        boundary: 'viewport',
    });

    const $form = $('#form-advertise');
    const parsley = $form.parsley();
    const $submitButton = $form.find(':submit').first();
    let pendingAdvertiseSubmit = false;
    let advertiseRequestInFlight = false;

    const setAdvertiseSubmitting = (isSubmitting) => {
        $submitButton.prop('disabled', isSubmitting);
    };

    const resetAdvertiseForm = () => {
        $form[0].reset();
        parsley.reset();
    };

    const submitAdvertiseForm = () => {
        if (!pendingAdvertiseSubmit || advertiseRequestInFlight) {
            return;
        }

        advertiseRequestInFlight = true;
        setAdvertiseSubmitting(true);

        /*
         * This bundle gets its own axios instance, so the `X-CSRF-TOKEN` default
         * set in `@common/bootstrap` doesn't apply -- the token travels as the
         * form's `_token` field instead.
         */
        axios.post($form.attr('action'), new FormData($form[0]), {
            headers: {
                Accept: 'application/json',
            },
        })
            .then(({ data }) => {
                trackAdvertiseSuccess();
                resetAdvertiseForm();
                openAdvertiseSuccessModal({
                    headline: data.headline,
                    message: data.message,
                });
            })
            .catch((err) => {
                const status = err.response?.status;
                const data = err.response?.data;

                if (status === 419) {
                    failure('Your session has expired. Please refresh the page and try again.');
                    return;
                }

                if (data?.errors) {
                    backendErrorHandler(data.errors, $form);
                    return;
                }

                if (data?.message) {
                    failure(data.message);
                    return;
                }

                errorHandler(err);
            })
            .finally(() => {
                advertiseRequestInFlight = false;
                pendingAdvertiseSubmit = false;
                setAdvertiseSubmitting(false);

                if (typeof grecaptcha !== 'undefined' && typeof grecaptcha.reset === 'function') {
                    grecaptcha.reset();
                }
            });
    };

    window.onGoogleReCaptchaCallback = function() {
        if (!pendingAdvertiseSubmit || advertiseRequestInFlight) {
            return;
        }

        submitAdvertiseForm();
    };

    $form.on('submit', function(event) {
        event.preventDefault();

        if (pendingAdvertiseSubmit) {
            return;
        }

        pendingAdvertiseSubmit = true;
        setAdvertiseSubmitting(true);
        parsley.reset();

        parsley.whenValidate()
            .done(() => {
                if (!pendingAdvertiseSubmit) {
                    return;
                }

                const abandonSubmit = () => {
                    pendingAdvertiseSubmit = false;
                    setAdvertiseSubmitting(false);
                    failure('Unable to verify reCAPTCHA. Please try again.');
                };

                /*
                 * Normally already resolved -- the focusin handler started this
                 * fetch when the user began filling the form -- but awaiting it
                 * covers submits that never fired a focus event (autofill,
                 * keyboard-only, a very fast paste-and-submit).
                 */
                loadRecaptcha()
                    .then(() => {
                        if (!pendingAdvertiseSubmit) {
                            return;
                        }

                        grecaptcha.execute();
                    })
                    .catch(abandonSubmit);
            })
            .fail(() => {
                pendingAdvertiseSubmit = false;
                setAdvertiseSubmitting(false);
            });
    });
});
